import { useEffect, useMemo, useState } from "react";
import { ChartCard, Skeleton } from "@/components/reusable";
import { fetchFieldCompleteness } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { FieldCompletenessDatum } from "@/types/analytics";

function rateBandFill(rate: number): string {
  if (rate >= 0.95) return "var(--color-brand-primary-dark)";
  if (rate >= 0.9) return "#378ADD";
  if (rate >= 0.75) return "var(--color-warning)";
  return "var(--color-error)";
}

function rateBandText(rate: number): string {
  if (rate >= 0.95) return "text-[var(--color-brand-primary-dark)]";
  if (rate >= 0.9) return "text-[#378ADD]";
  if (rate >= 0.75) return "text-[#854F0B]";
  return "text-[var(--color-error)]";
}

export function FieldCompletenessGrid() {
  const filters = useAnalyticsFilters();
  const [fields, setFields] = useState<FieldCompletenessDatum[] | null>(null);

  useEffect(() => {
    let active = true;
    setFields(null);
    fetchFieldCompleteness(filters).then((f) => {
      if (active) setFields(f);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  const belowThreshold = useMemo(
    () => fields?.filter((f) => f.populationRate < 0.9).length ?? 0,
    [fields]
  );

  return (
    <ChartCard
      title="Schedule field completeness"
      subtitle={`Population rate across the fleet · mandatory fields shown first`}
      headerRight={
        fields && belowThreshold > 0 ? (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide bg-[var(--color-warning-light)] text-[#854F0B]">
            {belowThreshold} fields below 90%
          </span>
        ) : null
      }
    >
      {!fields ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <Skeleton height="h-2.5" />
              <Skeleton height="h-1" />
              <Skeleton height="h-2.5" width="w-10" />
            </div>
          ))}
        </div>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-2">
          {fields.map((f) => (
            <li key={f.fieldName} className="flex flex-col gap-1">
              <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-tertiary)] truncate">
                {f.fieldName}
                {f.mandatory && (
                  <span
                    className="ml-1 text-[var(--color-error)]"
                    aria-label="mandatory field"
                    title="Mandatory field"
                  >
                    *
                  </span>
                )}
              </div>
              <div className="h-1 bg-[var(--color-bg-tertiary)] rounded-[2px] overflow-hidden">
                <div
                  className="h-full rounded-[2px] transition-[width] duration-500"
                  style={{
                    width: `${Math.round(f.populationRate * 100)}%`,
                    background: rateBandFill(f.populationRate),
                  }}
                />
              </div>
              <div
                className={`text-[11px] font-semibold ${rateBandText(f.populationRate)}`}
                style={{ fontFamily: "var(--font-mono)" }}
              >
                {Math.round(f.populationRate * 100)}%
              </div>
            </li>
          ))}
        </ul>
      )}
    </ChartCard>
  );
}
